import type { FieldGroup, FormField } from '@/types/formSchema';
import { isUnknownDateValue } from '@/utils/date';

/**
 * Parse a DOB string into [month, day, year].
 * Accepts the canonical yyyy-MM-dd as well as legacy MM-dd-yyyy.
 * Returns null if the string is invalid.
 */
function parseDobParts(dob: string): [number, number, number] | null {
  if (!dob) return null;

  const parts = dob.split('-');
  if (parts.length !== 3) return null;

  // Year-first (yyyy-MM-dd) is distinguished by a 4-digit leading segment; anything
  // else is treated as legacy month-first (MM-dd-yyyy).
  const isYearFirst = parts[0].length === 4;
  const month = parseInt(isYearFirst ? parts[1] : parts[0], 10);
  const day = parseInt(isYearFirst ? parts[2] : parts[1], 10);
  const year = parseInt(isYearFirst ? parts[0] : parts[2], 10);

  if (isNaN(month) || isNaN(day) || isNaN(year)) return null;

  return [month, day, year];
}

/**
 * Calculate age from date of birth string (MM-dd-yyyy format).
 * Returns age as a string, or empty string if DOB is invalid/empty/future.
 */
export function calculateAge(dob: string): string {
  // A DOB explicitly marked "Unknown" yields no age.
  if (isUnknownDateValue(dob)) return '';

  const parsed = parseDobParts(dob);
  if (!parsed) return '';

  const [month, day, year] = parsed;
  const birthDate = new Date(year, month - 1, day);
  const today = new Date();

  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();

  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }

  if (age < 0 || age > 150) return '';

  return String(age);
}

/**
 * Calculate initials from a full name.
 * Returns uppercase initials with periods (e.g., "John Doe" → "J. D.").
 */
export function calculateInitials(fullName: string): string {
  if (!fullName || typeof fullName !== 'string') return '';

  const trimmed = fullName.trim();
  if (!trimmed) return '';

  const initials = trimmed
    .split(/\s+/)
    .filter(word => word.length > 0)
    .map(word => `${word[0].toUpperCase()}.`)
    .join(' ');

  return initials;
}

/** Registry of auto-calculation functions keyed by inference type. */
const CALCULATORS_BY_TYPE: Record<string, (sourceValue: any) => string> = {
  age: calculateAge,
  initials: calculateInitials,
};

/**
 * Given a field with autoCalculates metadata and its current value,
 * compute the auto-calculated value for the target field.
 *
 * The type and label are resolved from the /schema API response,
 * so they are always available in field.autoCalculates.
 */
export function computeAutoCalculatedValue(
  field: FormField,
  sourceValue: any,
): { targetFieldId: string; value: string; label: string } | null {
  if (!field.autoCalculates) return null;

  const { field: targetFieldId, type, label } = field.autoCalculates;

  if (!type) {
    console.warn(`[autoCalculate] Field "${field.id}" missing autoCalculates.type`);
    return null;
  }

  if (!label) {
    console.warn(`[autoCalculate] Field "${field.id}" missing autoCalculates.label`);
    return null;
  }

  const calculator = CALCULATORS_BY_TYPE[type];

  if (!calculator) {
    console.warn(
      `[autoCalculate] No calculator registered for inference type "${type}" (field: ${field.id})`,
    );
    return null;
  }

  return {
    targetFieldId,
    value: calculator(sourceValue),
    label,
  };
}

/**
 * Collect all auto-calculated target field IDs from groups.
 * These are fields with `inferredFrom` set — they should be hidden from the form.
 */
export function getAutoCalculatedFieldIds(groups: FieldGroup[]): Set<string> {
  const ids = new Set<string>();

  groups.forEach(group => {
    group.fields.forEach(field => {
      if (field.inferredFrom) {
        ids.add(field.id);
      }
    });
  });

  return ids;
}

/**
 * Filter out auto-calculated target fields from a field list.
 * Fields with `inferredFrom` or `hidden: true` are removed.
 */
export function filterAutoCalculatedFields(
  fields: FormField[],
  autoCalculatedIds: Set<string>,
): FormField[] {
  return fields.filter(f => !autoCalculatedIds.has(f.id) && !f.hidden);
}

/**
 * Check if a field has valid mirror configuration.
 */
function hasMirrorConfig(field: FormField): boolean {
  return !!field.mirrorFieldId;
}

/**
 * Check if a value matches the mirror trigger condition.
 * Supports mirrorWhenValues (array) and mirrorWhenValue (single string).
 * When no condition fields are specified, always returns true (unconditional mirror).
 */
function matchesMirrorCondition(field: FormField, value: any): boolean {
  if (!field.mirrorWhenField) return true;
  const val = String(value ?? '');
  if (field.mirrorWhenValues?.length) return field.mirrorWhenValues.includes(val);
  if (field.mirrorWhenValue !== undefined) return val === field.mirrorWhenValue;
  return true;
}

/**
 * Return field updates for schema-driven mirroring. Handles two cases:
 * 1. The mirror trigger field changes to a trigger value → copy source field into target
 * 2. The source field changes while the mirror condition is already active → live-update target
 *
 * The trigger condition is defined per-field via mirrorWhenField + mirrorWhenValues/mirrorWhenValue.
 */
function applyMirrorField(
  field: FormField,
  formData: Record<string, any>,
  changedFieldId: string,
  changedValue: any,
  updates: Record<string, any>,
): void {
  if (!hasMirrorConfig(field)) return;

  // Case 1: trigger field just set to trigger value → copy source into target
  if (
    field.mirrorWhenField &&
    changedFieldId === field.mirrorWhenField &&
    matchesMirrorCondition(field, changedValue)
  ) {
    updates[field.id] = formData[field.mirrorFieldId!] ?? '';
  }

  // Case 2: source field changed while mirror is active → live-update target
  if (
    field.mirrorFieldId === changedFieldId &&
    matchesMirrorCondition(field, formData[field.mirrorWhenField!])
  ) {
    updates[field.id] = changedValue;
  }
}

export function getMirrorUpdates(
  groups: FieldGroup[],
  formData: Record<string, any>,
  changedFieldId: string,
  changedValue: any,
): Record<string, any> {
  const updates: Record<string, any> = {};

  for (const group of groups) {
    for (const field of group.fields) {
      applyMirrorField(field, formData, changedFieldId, changedValue, updates);
    }
  }

  // Mirrored fields may have autoCalculates (e.g. patient_full_name → patient_initials).
  // Compute derived values so callers don't need to handle this separately.
  const autoCalcUpdates: Record<string, any> = {};
  for (const [targetId, mirrorValue] of Object.entries(updates)) {
    const mirroredField = findFieldById(groups, targetId);
    if (mirroredField) {
      const result = computeAutoCalculatedValue(mirroredField, mirrorValue);
      if (result) {
        autoCalcUpdates[result.targetFieldId] = result.value;
      }
    }
  }

  return { ...updates, ...autoCalcUpdates };
}

/**
 * Find a field by ID across all groups.
 */
export function findFieldById(groups: FieldGroup[], fieldId: string): FormField | undefined {
  for (const group of groups) {
    const found = group.fields.find(f => f.id === fieldId);
    if (found) return found;
  }
  return undefined;
}
